// packages/web/src/components/service/ServiceSpecs.tsx
// Auto-derived spec pills — no seller input required.

import { Box, Chip } from '@mui/material';
import React from 'react';

import type { MarketplaceService } from '../../types/pricing';

interface Spec {
  icon: string;
  label: string;
}

/** Extract speed range from variant names e.g. "50 Mbps", "1 Gbps" */
function extractSpeedRange(service: MarketplaceService): string | null {
  const variants = (service as any).variants?.variants || [];
  const speeds: number[] = []; // in Mbps

  variants.forEach((v: any) => {
    const name: string = (v.name || v.label || '').toLowerCase();
    const gbps = name.match(/(\d+(?:\.\d+)?)\s*g(?:bps|b\/s)?/);
    const mbps = name.match(/(\d+(?:\.\d+)?)\s*m(?:bps|b\/s)?/);
    if (gbps) speeds.push(parseFloat(gbps[1]) * 1000);
    else if (mbps) speeds.push(parseFloat(mbps[1]));
  });

  if (speeds.length === 0) return null;
  const min = Math.min(...speeds);
  const max = Math.max(...speeds);
  const fmt = (n: number) => (n >= 1000 ? `${n / 1000}G` : `${n}M`);
  return min === max ? fmt(min) : `${fmt(min)}–${fmt(max)}`;
}

/** Category-based defaults for specs */
function getCategoryDefaults(category: string, metadata: any): Spec[] {
  const cat = (category || '').toLowerCase();
  const specs: Spec[] = [];

  if (cat.includes('dedicated fiber') || cat.includes('dfi') || cat.includes('sdfi')) {
    specs.push({ icon: '↕', label: metadata?.symmetric !== false ? 'Symmetric' : 'Asymmetric' });
    specs.push({ icon: '✓', label: metadata?.uptime_sla || '99.99% SLA' });
    specs.push({ icon: '🏢', label: 'Business-grade' });
  } else if (cat.includes('ethernet')) {
    specs.push({ icon: '↕', label: 'Symmetric' });
    specs.push({ icon: '✓', label: metadata?.uptime_sla || '99.99% SLA' });
    specs.push({ icon: '🔗', label: 'Dedicated circuit' });
  } else if (cat.includes('internet') || cat.includes('broadband')) {
    specs.push({ icon: '✓', label: metadata?.uptime_sla || '99.9% SLA' });
    specs.push({ icon: '📡', label: 'Managed router' });
  } else if (cat.includes('voice') || cat.includes('sip') || cat.includes('trunking')) {
    specs.push({ icon: '🎙', label: 'HD Voice' });
    specs.push({ icon: '🚨', label: 'E911 included' });
  } else if (cat.includes('unified') || cat.includes('calling') || cat.includes('collaboration')) {
    specs.push({ icon: '💬', label: 'HD Voice & Video' });
    specs.push({ icon: '🔗', label: 'Works with existing phones' });
  } else if (cat.includes('cloud') || cat.includes('saas')) {
    specs.push({ icon: '☁️', label: 'Cloud-delivered' });
    specs.push({ icon: '🔒', label: 'Enterprise security' });
  } else {
    specs.push({ icon: '✓', label: 'Business-grade' });
  }

  // Static IP from metadata
  if (metadata?.static_ip) {
    specs.push({ icon: '🔒', label: 'Static IP included' });
  }

  return specs;
}

const ServiceSpecs: React.FC<{ service: MarketplaceService }> = ({ service }) => {
  const metadata = (service as any).metadata || {};
  const specs: Spec[] = [];

  // 1. Speed range — derived from variants
  const speedRange = extractSpeedRange(service);
  if (speedRange) {
    specs.push({ icon: '⚡', label: speedRange });
  }

  // 2. Contract term — from service data
  const terms = (service as any).contract_term_months;
  if (terms && terms.length > 0) {
    const termLabel =
      terms.length === 1
        ? `${terms[0]} mo term`
        : `${Math.min(...terms)}–${Math.max(...terms)} mo terms`;
    specs.push({ icon: '📋', label: termLabel });
  }

  // 3. Category-based defaults + metadata overrides
  const categorySpecs = getCategoryDefaults(service.category || '', metadata);
  specs.push(...categorySpecs);

  // 4. Installation timeline (from category)
  const cat = (service.category || '').toLowerCase();
  let installTime: string | null = null;
  if (cat.includes('fiber') || cat.includes('ethernet') || cat.includes('dfi')) {
    installTime = metadata?.typical_install_days
      ? `~${metadata.typical_install_days} day install`
      : '~30 day install';
  } else if (cat.includes('voice') || cat.includes('sip')) {
    installTime = '~7–14 day setup';
  } else if (cat.includes('cloud') || cat.includes('calling') || cat.includes('saas')) {
    installTime = '~1–3 day setup';
  }
  if (installTime) {
    specs.push({ icon: '📅', label: installTime });
  }

  if (specs.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {specs.map((spec, i) => (
          <Chip
            key={i}
            label={`${spec.icon} ${spec.label}`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              borderColor: '#e2e8f0',
              color: '#475569',
              backgroundColor: '#f8fafc',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default React.memo(ServiceSpecs);
